import SecondaryButton from 'core-system/Button/SecondaryButton'
import Dropdown from 'core-system/Dropdown'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import Text from 'core-system/Text'
import TextField from 'core-system/TextField'
import palette from 'core-system/Themes/palette'
import pxToRem from 'core-system/utils/pxToRem'
import React, { useEffect, useMemo, useState } from 'react'
import {
  FleetCardReplacementReason,
  FleetCardSummary,
} from 'redux/employee/employeeTypes'
import styled from 'styled-components'
import {
  getFleetCardPillLabel,
  getFleetCardReplacementReasonItems,
  getFleetCardShippingStatusLabel,
} from '../utils'

const Subtitle = styled(Text)`
  margin-top: -0.5rem;
  margin-bottom: 1.25rem;
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ReasonField = styled.div`
  margin-top: 0.25rem;
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`

export type FleetCardManageAction = 'activate' | 'deactivate' | 'replace'

interface ManageFleetCardModalProps {
  open: boolean
  card: FleetCardSummary | null
  shippingAddress?: string | null
  isSubmitting?: boolean
  onClose: () => void
  onAction?: (
    action: FleetCardManageAction,
    card: FleetCardSummary,
    options?: {
      replacementReason?: FleetCardReplacementReason
      shippingAddress?: string
    }
  ) => void
}

const ManageFleetCardModal = React.memo((props: ManageFleetCardModalProps) => {
  const {
    open,
    card,
    shippingAddress = null,
    isSubmitting = false,
    onClose,
    onAction,
  } = props
  const [replacementReason, setReplacementReason] =
    useState<FleetCardReplacementReason | null>(null)
  const [shipTo, setShipTo] = useState(shippingAddress || '')

  const reasonItems = useMemo(
    () => (card ? getFleetCardReplacementReasonItems(card) : []),
    [card]
  )

  useEffect(() => {
    setReplacementReason(null)
    setShipTo(shippingAddress || '')
  }, [open, card?.internalId, shippingAddress])

  if (!card) {
    return null
  }

  const handleAction = (action: FleetCardManageAction) => {
    if (isSubmitting) {
      return
    }
    if (action === 'replace') {
      if (!replacementReason) {
        return
      }
      if (card.type === 'physical' && !shipTo.trim()) {
        return
      }
      onAction?.(action, card, {
        replacementReason,
        ...(card.type === 'physical'
          ? { shippingAddress: shipTo.trim() }
          : {}),
      })
      return
    }
    onAction?.(action, card)
  }

  return (
    <Modal open={open} onClose={onClose} width={pxToRem(420)}>
      <ModalHeader title='Manage Fleet Card' onClose={onClose} />
      <ModalBody>
        <Subtitle variant='body1' textColor={palette.text.secondary}>
          {getFleetCardPillLabel(card)} card actions
        </Subtitle>
        <Actions>
          {card.type === 'physical' ? (
            <Text variant='body1'>
              Shipping status:{' '}
              {getFleetCardShippingStatusLabel(card.shippingStatus)}
            </Text>
          ) : null}
          <SecondaryButton
            onClick={() => handleAction('activate')}
            disabled={isSubmitting || card.status === 'active'}
          >
            Activate
          </SecondaryButton>
          <SecondaryButton
            onClick={() => handleAction('deactivate')}
            disabled={isSubmitting || card.status === 'inactive'}
          >
            Deactivate
          </SecondaryButton>
          <ReasonField>
            <Dropdown
              label='Replacement reason'
              placeholder='Select a reason...'
              active={replacementReason}
              items={reasonItems}
              itemCallback={(item) =>
                setReplacementReason(item.id as FleetCardReplacementReason)
              }
              disabled={isSubmitting}
              width='100%'
            />
          </ReasonField>
          {card.type === 'physical' ? (
            <TextField
              label='Ship to'
              value={shipTo}
              onChange={(e) => setShipTo(e.currentTarget.value)}
              disabled={isSubmitting}
              width='100%'
            />
          ) : null}
          <SecondaryButton
            onClick={() => handleAction('replace')}
            disabled={
              isSubmitting ||
              !replacementReason ||
              (card.type === 'physical' && !shipTo.trim())
            }
          >
            Replace
          </SecondaryButton>
        </Actions>
      </ModalBody>
      <ModalFooter>
        <FooterActions>
          <SecondaryButton onClick={onClose} disabled={isSubmitting}>
            Close
          </SecondaryButton>
        </FooterActions>
      </ModalFooter>
    </Modal>
  )
})

if (process.env.NODE_ENV !== 'production') {
  ManageFleetCardModal.displayName = 'ManageFleetCardModal'
}

export default ManageFleetCardModal
